import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const SUPPORTED_LANGS = ["en", "hi", "kn", "ta", "ml", "te", "bn", "mr", "gu", "other"];

const empty = (categoryId = "") => ({
  names: {},
  description: {},
  price: 0,
  compare_price: 0,
  image_url: "",
  category_id: categoryId,
  concern_tags: [],
  sort: 0,
  active: true,
});

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [concerns, setConcerns] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());

  const load = async () => {
    const [p, c, cn] = await Promise.all([
      api.get("/admin/products"),
      api.get("/admin/categories"),
      api.get("/admin/concerns"),
    ]);
    setProducts(p.data);
    setCategories(c.data);
    setConcerns(cn.data);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing("new"); setForm(empty(categories[0]?.id || "")); };
  const startEdit = (p) => {
    setEditing(p.id);
    setForm({
      names: p.names || {},
      description: p.description || {},
      price: p.price,
      compare_price: p.compare_price || 0,
      image_url: p.image_url,
      category_id: p.category_id,
      concern_tags: p.concern_tags || [],
      sort: p.sort || 0,
      active: p.active,
    });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, compare_price: form.compare_price || null };
      if (editing === "new") await api.post("/admin/products", payload);
      else await api.patch(`/admin/products/${editing}`, payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    load();
  };

  const toggleConcern = (cid) => {
    setForm((f) => ({
      ...f,
      concern_tags: f.concern_tags.includes(cid) ? f.concern_tags.filter((x) => x !== cid) : [...f.concern_tags, cid],
    }));
  };

  const concernsForCategory = concerns.filter((c) => c.category_id === form.category_id);

  return (
    <div className="p-8 lg:p-12 max-w-6xl">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="overline">Catalogue</p>
          <h1 className="editorial-h text-5xl text-brand-dark mt-2">Products</h1>
        </div>
        <Button onClick={startNew} data-testid="product-new-btn" className="rounded-none bg-brand-primary hover:bg-brand-primaryHover text-brand-bone gap-2">
          <Plus strokeWidth={1.5} className="w-4 h-4" /> Add product
        </Button>
      </div>

      {editing && (
        <form onSubmit={save} className="mt-8 bg-white border border-brand-line p-6 space-y-6" data-testid="product-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="overline">Image URL</Label>
              <Input className="mt-2 h-11 rounded-none border-brand-line" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="product-form-image" required />
              {form.image_url && <img src={form.image_url} alt="" className="mt-2 w-24 h-24 object-cover bg-brand-elevated" />}
            </div>
            <div>
              <Label className="overline">Category</Label>
              <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value, concern_tags: [] })} className="mt-2 w-full h-11 border border-brand-line bg-white px-3 text-sm" data-testid="product-form-category">
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.labels?.en || c.slug}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="overline">Price (₹)</Label>
              <Input type="number" className="mt-2 h-11 rounded-none border-brand-line" value={form.price} onChange={(e) => setForm({ ...form, price: Number(e.target.value) })} required data-testid="product-form-price" />
            </div>
            <div>
              <Label className="overline">Compare-at price (₹)</Label>
              <Input type="number" className="mt-2 h-11 rounded-none border-brand-line" value={form.compare_price} onChange={(e) => setForm({ ...form, compare_price: Number(e.target.value) })} data-testid="product-form-compare" />
            </div>
            <div>
              <Label className="overline">Sort</Label>
              <Input type="number" className="mt-2 h-11 rounded-none border-brand-line" value={form.sort} onChange={(e) => setForm({ ...form, sort: Number(e.target.value) })} />
            </div>
            <div className="flex items-center gap-3 pt-7">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="product-form-active" />
              <Label className="font-sans text-sm">Active</Label>
            </div>
          </div>

          <div>
            <Label className="overline">Name (per language)</Label>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2">
              {SUPPORTED_LANGS.map((l) => (
                <div key={l}>
                  <p className="text-[10px] uppercase tracking-widest text-brand-muted">{l}</p>
                  <Input
                    className="h-10 rounded-none border-brand-line"
                    value={form.names[l] || ""}
                    onChange={(e) => setForm({ ...form, names: { ...form.names, [l]: e.target.value } })}
                    placeholder={l === "en" ? "English name (required)" : "Optional"}
                    required={l === "en"}
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="overline">Concern tags</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {concernsForCategory.map((cn) => {
                const active = form.concern_tags.includes(cn.id);
                return (
                  <button
                    type="button"
                    key={cn.id}
                    onClick={() => toggleConcern(cn.id)}
                    className={`text-xs px-3 py-1.5 border ${active ? "bg-brand-primary text-brand-bone border-brand-primary" : "border-brand-line text-brand-dark"}`}
                  >
                    {cn.labels?.en || cn.slug}
                  </button>
                );
              })}
              {concernsForCategory.length === 0 && <p className="text-xs text-brand-muted">No concerns in this category.</p>}
            </div>
          </div>

          <div className="flex gap-3 justify-end">
            <Button type="button" variant="outline" onClick={() => setEditing(null)} className="rounded-none border-brand-line">Cancel</Button>
            <Button type="submit" className="rounded-none bg-brand-primary text-brand-bone" data-testid="product-form-submit">Save</Button>
          </div>
        </form>
      )}

      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map((p) => (
          <div key={p.id} className="bg-white border border-brand-line p-4" data-testid={`product-card-admin-${p.id}`}>
            <div className="flex gap-3">
              <img src={p.image_url} alt="" className="w-20 h-20 object-cover bg-brand-elevated" />
              <div className="flex-1 min-w-0">
                <p className="font-serif text-xl text-brand-dark leading-tight">{p.names?.en}</p>
                <p className="font-sans text-sm font-semibold text-brand-dark mt-1">₹{p.price} {p.compare_price ? <span className="text-xs text-brand-muted line-through ml-1">₹{p.compare_price}</span> : null}</p>
                <p className="text-xs text-brand-muted mt-1">{p.active ? "Active" : "Paused"}</p>
              </div>
            </div>
            <div className="flex gap-2 mt-3 justify-end">
              <button onClick={() => startEdit(p)} className="text-xs text-brand-dark hover:text-brand-primary flex items-center gap-1" data-testid={`product-edit-${p.id}`}><Pencil strokeWidth={1.5} className="w-3 h-3" /> Edit</button>
              <button onClick={() => remove(p.id)} className="text-xs text-brand-dark hover:text-brand-primary flex items-center gap-1" data-testid={`product-delete-${p.id}`}><Trash2 strokeWidth={1.5} className="w-3 h-3" /> Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
